import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import ImagePicker from '@/components/feature/ImagePicker';
import BlogRichEditor from '@/components/feature/BlogRichEditor';
import {
  formatArticleDate,
  getVisitCounts,
  listAllArticles,
  slugify,
  tagList,
  type BlogArticle,
  type VisitCounts,
} from '@/lib/blog';

interface Draft {
  id: number | null;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  cover_image: string;
  tags: string;
  status: 'draft' | 'published';
}

const EMPTY: Draft = {
  id: null,
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  cover_image: '',
  tags: '',
  status: 'draft',
};

const STATUS_META: Record<string, { label: string; className: string }> = {
  published: { label: 'Publié', className: 'bg-primary-100 text-primary-700' },
  draft: { label: 'Brouillon', className: 'bg-background-200/70 text-foreground-600' },
};

export default function SuperAdminBlogPage() {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [counts, setCounts] = useState<VisitCounts>({ total: 0, byArticle: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [deleteTarget, setDeleteTarget] = useState<BlogArticle | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, visits] = await Promise.all([listAllArticles(), getVisitCounts()]);
      setArticles(list);
      setCounts(visits);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les articles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articles.filter((a) => {
      if (filter !== 'all' && (a.status || 'draft') !== filter) return false;
      if (!q) return true;
      return `${a.title} ${a.slug || ''} ${a.tags || ''}`.toLowerCase().includes(q);
    });
  }, [articles, search, filter]);

  const openNew = () => setDraft({ ...EMPTY });

  const openEdit = (a: BlogArticle) => {
    setDraft({
      id: a.id,
      title: a.title || '',
      slug: a.slug || '',
      excerpt: a.excerpt || '',
      content: a.content || '',
      cover_image: a.cover_image || '',
      tags: a.tags || '',
      status: (a.status as 'draft' | 'published') || 'draft',
    });
  };

  const save = async (publish?: boolean) => {
    if (!draft) return;
    if (!draft.title.trim()) {
      setError('Le titre est obligatoire.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const status = publish === undefined ? draft.status : publish ? 'published' : 'draft';
      const slug = (draft.slug.trim() || slugify(draft.title)).replace(/^-+|-+$/g, '');
      const payload: Record<string, unknown> = {
        title: draft.title.trim(),
        slug,
        excerpt: draft.excerpt.trim() || null,
        content: draft.content,
        cover_image: draft.cover_image.trim() || null,
        tags: draft.tags.trim() || null,
        status,
        updated_at: new Date().toISOString(),
      };
      if (status === 'published') {
        payload.published_at = new Date().toISOString();
      }

      if (draft.id) {
        const { error: upErr } = await supabase.from('blog_articles').update(payload).eq('id', draft.id);
        if (upErr) throw upErr;
      } else {
        const { error: insErr } = await supabase.from('blog_articles').insert(payload);
        if (insErr) throw insErr;
      }
      setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a: BlogArticle) => {
    try {
      await supabase.from('blog_articles').delete().eq('id', a.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Suppression impossible.');
    }
  };

  const togglePublish = async (a: BlogArticle) => {
    try {
      const next = (a.status || 'draft') === 'published' ? 'draft' : 'published';
      await supabase
        .from('blog_articles')
        .update({
          status: next,
          published_at: next === 'published' ? new Date().toISOString() : a.published_at,
          updated_at: new Date().toISOString(),
        })
        .eq('id', a.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mise à jour impossible.');
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Blog</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Rédigez les articles publiés sur zifek.fr/blog et suivez leur audience.
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>Nouvel article
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] uppercase tracking-wider text-foreground-400 font-semibold">Articles</p>
          <p className="text-xl font-bold font-heading text-foreground-950 mt-1">{articles.length}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] uppercase tracking-wider text-foreground-400 font-semibold">Publiés</p>
          <p className="text-xl font-bold font-heading text-primary-700 mt-1">
            {articles.filter((a) => a.status === 'published').length}
          </p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] uppercase tracking-wider text-foreground-400 font-semibold">Brouillons</p>
          <p className="text-xl font-bold font-heading text-foreground-700 mt-1">
            {articles.filter((a) => (a.status || 'draft') !== 'published').length}
          </p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] uppercase tracking-wider text-foreground-400 font-semibold">Lectures</p>
          <p className="text-xl font-bold font-heading text-accent-700 mt-1">{counts.total.toLocaleString('fr-FR')}</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un article…"
            className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300"
          />
        </div>
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          {(['all', 'published', 'draft'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                filter === f ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {f === 'all' ? 'Tous' : f === 'published' ? 'Publiés' : 'Brouillons'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center bg-background-50 border border-background-200/70 rounded-lg">
          <i className="ri-article-line text-3xl text-foreground-300"></i>
          <p className="mt-3 text-sm text-foreground-600">Aucun article pour le moment.</p>
          <button onClick={openNew} className="mt-4 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer">
            Écrire le premier article
          </button>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg divide-y divide-background-200/70 overflow-hidden">
          {filtered.map((a) => {
            const meta = STATUS_META[(a.status || 'draft') as string] || STATUS_META.draft;
            return (
              <div key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
                <div className="w-full sm:w-16 h-16 rounded-md overflow-hidden bg-background-100 flex-shrink-0">
                  {a.cover_image ? (
                    <img src={a.cover_image} alt="" className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground-300">
                      <i className="ri-image-line"></i>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground-950 truncate">{a.title}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.className}`}>{meta.label}</span>
                  </div>
                  <p className="text-[11px] text-foreground-400 truncate mt-0.5">
                    /blog/{a.slug || a.id} · {formatArticleDate(a.published_at || a.created_at) || 'non daté'}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {tagList(a.tags).map((t) => (
                      <span key={t} className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-900 text-[10px]">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-700 whitespace-nowrap">
                    <i className="ri-eye-line text-accent-600"></i>
                    {(counts.byArticle[a.id] || 0).toLocaleString('fr-FR')}
                  </span>
                  <button
                    onClick={() => togglePublish(a)}
                    title={(a.status || 'draft') === 'published' ? 'Dépublier' : 'Publier'}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className={(a.status || 'draft') === 'published' ? 'ri-eye-off-line' : 'ri-send-plane-line'}></i>
                  </button>
                  <button
                    onClick={() => openEdit(a)}
                    title="Modifier"
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => setDeleteTarget(a)}
                    title="Supprimer"
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDraft(null)}></div>
          <div className="relative bg-background-50 w-full sm:max-w-3xl max-h-[92vh] rounded-t-2xl sm:rounded-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
              <h3 className="text-sm font-bold text-foreground-950">
                {draft.id ? 'Modifier l’article' : 'Nouvel article'}
              </h3>
              <button onClick={() => setDraft(null)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Titre *</label>
                <input
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value, slug: draft.slug || slugify(e.target.value) })}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300"
                  placeholder="Ex : Facturer correctement au Maroc"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Lien (slug)</label>
                  <input
                    value={draft.slug}
                    onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })}
                    className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300"
                    placeholder="facturer-correctement-au-maroc"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Étiquettes (virgules)</label>
                  <input
                    value={draft.tags}
                    onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                    className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300"
                    placeholder="Facturation, Conformité"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Image de couverture</label>
                <div className="flex items-center gap-2">
                  <input
                    value={draft.cover_image}
                    onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })}
                    className="flex-1 px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300"
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors flex items-center gap-1.5"
                  >
                    <i className="ri-image-add-line"></i>Médiathèque
                  </button>
                </div>
                {draft.cover_image && (
                  <div className="mt-2 w-40 h-24 rounded-md overflow-hidden border border-background-200/70">
                    <img src={draft.cover_image} alt="" className="w-full h-full object-cover object-top" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Résumé</label>
                <textarea
                  value={draft.excerpt}
                  onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300 resize-none"
                  placeholder="Une phrase qui donne envie de lire"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">
                  Contenu
                </label>
                <BlogRichEditor
                  value={draft.content}
                  onChange={(html) => setDraft({ ...draft, content: html })}
                  placeholder="Rédigez l’article. Ajoutez images, vidéos, titres et citations avec la barre d’outils."
                />
              </div>
            </div>

            <div className="p-4 border-t border-background-200/70 flex flex-col sm:flex-row gap-2 flex-shrink-0">
              <button
                onClick={() => save(false)}
                disabled={saving}
                className="flex-1 h-11 rounded-full border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                Enregistrer le brouillon
              </button>
              <button
                onClick={() => save(true)}
                disabled={saving}
                className="flex-1 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                Publier
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-sm p-5">
            <h3 className="text-sm font-bold text-foreground-950">Supprimer cet article ?</h3>
            <p className="mt-1.5 text-sm text-foreground-600">
              « {deleteTarget.title} » sera définitivement supprimé, ainsi que son lien public.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 h-10 rounded-full border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={() => remove(deleteTarget)}
                className="flex-1 h-10 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      <ImagePicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => setDraft((prev) => (prev ? { ...prev, cover_image: url } : prev))}
        selectedUrl={draft?.cover_image}
      />
    </div>
  );
}